import {
  Injectable,
  Injector,
  ComponentFactoryResolver,
  EmbeddedViewRef,
  ApplicationRef,
  ComponentRef,
} from "@angular/core";

import { AlertComponent, AlertType } from "./alert.component";

export type ComponentEntity = {
  component: ComponentRef<AlertComponent>;
  domParent: HTMLElement
}

@Injectable()
export class AlertService {
  componentsQueue : ComponentEntity[] = [];

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
  ) {}

  private addComponent(): ComponentRef<AlertComponent> {
    return this.componentFactoryResolver.resolveComponentFactory(AlertComponent).create(this.injector);
  }

  private removeComponent(ref: ComponentRef<AlertComponent>) {
    ref.hostView.destroy();
  }

  /**
   * Display alert component or add it to queue to display
   * after previous alert is closed
   * @param message - Text message to display
   * @param type - Type of alert Success/Error/Warning/Info
   * @param autoClose
   * @param domParent
   * @param ctaMessage
   * @param click
   */
  public open(
    message: string,
    type: AlertType,
    autoClose: boolean = true,
    domParent?: HTMLElement,
    ctaMessage: string = "",
    click?: () => void
  ): ComponentRef<any> {
    if (domParent == undefined) {
      // TODO: Get the elementId from a provider registered in the app.module.ts
      domParent = document.getElementById("portal-outlet__alert");
    }
    const component: ComponentRef<AlertComponent> = this.addComponent();

    const componentInstance = component.instance as AlertComponent;
    componentInstance.message = message;
    componentInstance.type = type;
    componentInstance.autoClose = autoClose;
    componentInstance.ctaMessage = ctaMessage;
    componentInstance.click = click;

    const componentEntity = {component, domParent}
    // If queue is empty, display alert immediately and add to queue,
    // otherwise add to queue
    if(this.componentsQueue.length === 0){
      this.addAlertToQueue(componentEntity)
      this.displayAlert(componentEntity);
    }else{
      this.addAlertToQueue(componentEntity)
    }
    return component;
  }

  public close(ref: ComponentRef<AlertComponent>) {
    this.destroy(ref);
  }

  private displayAlert(item: ComponentEntity) {
    this.appRef.attachView(item.component.hostView);
    const domElem = (item.component.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    item.domParent.insertBefore(domElem, item.domParent.firstChild);
    //TODO: Fix the element into the document instead of scrolling to it
    domElem.scrollIntoView({ block: "end", behavior: "smooth" });
  }

  private destroy(ref: ComponentRef<AlertComponent>) {
    this.appRef.detachView(ref.hostView);
    ref.hostView.destroy();
    this.removeComponent(ref);
  }

  /**
   * Adding alert to queue
   */
  public addAlertToQueue(item: ComponentEntity) {
    this.componentsQueue.push(item);
  }

  /**
   * Removing alert from queue and display next alert in queue
   */
  public removeAlertFromQueue() {
    this.componentsQueue.shift();
    if(this.componentsQueue.length > 0){
      this.displayAlert(this.componentsQueue[0]);
    }
  }

}

// export function parentNode(node: HTMLElement) {
//     return { provide: CmxAlertService, deps: [ComponentFactoryResolver, ApplicationRef, Injector], useFactory: (cfr, ar, i) => new CmxAlertService(cfr, ar, i).setNode()}
// }
